import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { CroppedImage } from '../components/common/ImageCropUploader';
import { cn } from '../lib/utils';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { PaymentDialog } from '../components/common/PaymentDialog';
import { DiscountControl } from '../components/common/DiscountControl';
import { ItemEditDialog } from '../components/common/ItemEditDialog';
import { useConfirm } from '../context/ConfirmContext';
import { requestBillAndPrint } from '../lib/receipt';
import { KITCHEN_STATUS, kitchenStatusLabel } from '../lib/constants';
import { Search, Plus, Minus, Trash2, ShoppingCart, Store, Pencil, Ban, Send, Receipt, CreditCard } from 'lucide-react';

const fmtEur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;

export default function StoreSalePage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [order, setOrder] = useState(null);   // ordine banco reale (senza tavolo)
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const scanRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/store/pos-products', { params: { q: search || undefined } }); setProducts((data || []).filter((p) => p.available !== false)); }
    catch { /* noop */ }
  }, [search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/categories').then(({ data }) => setCategories(data || [])).catch(() => {}); }, []);
  useEffect(() => { const tm = setTimeout(() => { if (scanRef.current) scanRef.current.focus(); }, 300); return () => clearTimeout(tm); }, []);

  // Solo le categorie (del menu) che hanno almeno un prodotto "negozio".
  const catsWithProducts = categories.filter((c) => products.some((p) => p.category_id === c.id));
  const shownProducts = activeCat === 'all' ? products : products.filter((p) => p.category_id === activeCat);

  // Crea l'ordine banco solo al primo prodotto aggiunto (lazy), come per i tavoli.
  const ensureOrder = async () => {
    if (order?.id) return order;
    const { data } = await api.post('/orders/open-counter');
    setOrder(data);
    return data;
  };

  const addProduct = async (product, opts = {}) => {
    try {
      const ord = await ensureOrder();
      const { data } = await api.post(`/orders/${ord.id}/items`, {
        product_id: product.id, qty: opts.qty || 1,
        variant: opts.variant || null, extras: opts.extras || [], notes: opts.notes || '',
      });
      setOrder(data);
    } catch (e) { toast.error(t('order.addProductError')); }
  };

  const clickProduct = (product) => { addProduct(product); };

  const handleScan = async (code) => {
    try {
      const { data } = await api.get('/store/pos-scan', { params: { code } });
      await addProduct(data.product);
      toast.success(t('sale.itemAdded', { name: data.product.name }));
    } catch (e) { toast.error(t('sale.notFound')); }
  };
  const onScanInput = (e) => {
    if (e.key === 'Enter' && search.trim()) { e.preventDefault(); handleScan(search.trim()); setSearch(''); }
  };

  const updateQty = async (item, delta) => {
    const nq = item.qty + delta;
    if (nq <= 0) return removeItem(item);
    const { data } = await api.put(`/orders/${order.id}/items/${item.id}`, { qty: nq });
    setOrder(data);
  };

  const removeItem = async (item, sent = false) => {
    if (sent) {
      const ok = await confirm({
        title: t('order.voidConfirmTitle', { name: item.name }),
        description: t('order.voidConfirmDesc'),
        destructive: true, confirmText: t('order.void'),
      });
      if (!ok) return;
    }
    try {
      const { data } = await api.delete(`/orders/${order.id}/items/${item.id}`);
      setOrder(data);
      if (sent) toast.success(t('order.itemVoided'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.cannotRemove')); }
  };

  // "Conferma ordine" = stesso endpoint di invio dei tavoli (blocca gli articoli).
  const confirmOrder = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/send`);
      setOrder(data);
      toast.success(t('sale.orderConfirmed', { defaultValue: 'Ordine confermato' }));
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.noNewItems')); }
  };

  const requestBill = async () => {
    try {
      const via = await requestBillAndPrint(api, order.id);
      toast.success(via === 'agent' ? t('order.billSentToPrinter') : t('order.billPrinting'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.billError')); }
  };

  const onPaid = (res) => {
    if (res.order_closed) {
      toast.success(t('sale.saleCompleted', { defaultValue: 'Vendita completata' }));
      setOrder(null); setPayOpen(false); load();
      if (scanRef.current) scanRef.current.focus();
    }
  };

  const items = order?.items || [];
  const pendingCount = items.reduce(
    (s, i) => s + Math.max(0, Number(i.qty || 0) - Number(i.sent_qty || 0)), 0);

  return (
    <div className="h-full flex flex-col p-4 md:p-6 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4">
        {/* Articoli */}
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {/* Barra categorie (scorrevole) — "Tutti" + categorie con prodotti negozio */}
          {catsWithProducts.length > 0 && (
            <ScrollArea className="w-full whitespace-nowrap shrink-0">
              <div className="flex gap-2 pb-1">
                <button
                  onClick={() => setActiveCat('all')}
                  data-testid="sale-cat-all"
                  className={cn('shrink-0 rounded-full border px-4 h-9 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    activeCat === 'all' ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}
                >
                  {t('sale.allCategories', { defaultValue: 'Tutti' })}
                </button>
                {catsWithProducts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`sale-cat-${c.id}`}
                    className={cn('shrink-0 rounded-full border px-4 h-9 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      activeCat === c.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5 pb-1">
            {shownProducts.map((p) => (
              <button key={p.id} onClick={() => clickProduct(p)} className="group overflow-hidden rounded-xl border border-border bg-card text-left hover:border-primary hover:shadow-sm-token transition-colors active:scale-[0.98] flex flex-col" data-testid={`sale-product-${p.id}`}>
                <div className="relative">
                  {p.image ? (
                    <CroppedImage data={p} aspect="1 / 1" draggable={false} />
                  ) : (
                    <div className="flex items-center justify-center bg-muted/50 text-muted-foreground" style={{ aspectRatio: '1 / 1' }}>
                      <Store className="h-7 w-7 opacity-40" />
                    </div>
                  )}
                  <span className="absolute left-2 top-2 rounded-full bg-background/85 backdrop-blur-sm px-2.5 py-1 text-sm font-bold text-primary shadow-sm-token" data-testid={`sale-product-price-${p.id}`}>{fmtEur(p.price)}</span>
                </div>
                <div className="p-2.5">
                  <div className="font-medium text-sm leading-tight line-clamp-2 text-center">{p.name}</div>
                </div>
              </button>
            ))}
            {!shownProducts.length && <div className="col-span-full text-center text-muted-foreground py-10">{t('sale.noItems')}</div>}
            </div>
          </div>
        </div>

        {/* Colonna destra: ricerca barcode (in cima) + carrello */}
        <div className="w-full lg:w-[360px] shrink-0 min-h-0 flex flex-col gap-3">
          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input ref={scanRef} className="pl-10" placeholder={t('sale.scanPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onScanInput} data-testid="sale-scan-input" />
          </div>
          {/* Carrello / Ordine (stesse funzioni dei tavoli) */}
          <div className="flex-1 min-h-0 rounded-2xl border border-border bg-card flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 border-b border-border h-14 shrink-0">
            <ShoppingCart className="h-4 w-4 text-primary" />
            <span className="font-semibold text-base">{t('order.order')}</span>
            <span className="ml-auto text-[11px] text-muted-foreground">{items.length} {t('order.items')}</span>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 space-y-2">
              {items.length === 0 && <div className="text-center text-muted-foreground py-10 text-sm">{t('order.noItems')}</div>}
              {items.map((it) => {
                const ks = KITCHEN_STATUS[it.kitchen_status] || KITCHEN_STATUS.pending;
                const sentQty = Number(it.sent_qty || 0);
                const hasPending = Number(it.qty || 0) > sentQty;
                const anySent = sentQty > 0;
                return (
                  <div key={it.id} className="rounded-xl border border-border p-3" data-testid={`cart-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-base">{it.name}{it.variant ? ` (${it.variant})` : ''}</div>
                        {it.extras?.length > 0 && <div className="text-sm text-muted-foreground">+ {it.extras.map((e) => e.name).join(', ')}</div>}
                        {it.notes && <div className="text-sm text-accent">{t('order.note')}: {it.notes}</div>}
                        <div className={cn('text-xs mt-0.5', ks.color)}>{kitchenStatusLabel(t, it.kitchen_status)}</div>
                        {anySent && hasPending && (
                          <div className="text-[11px] text-muted-foreground" data-testid="cart-item-partial">{sentQty} {t('order.sentQty', { defaultValue: 'inviati' })} · {Number(it.qty) - sentQty} {t('order.newQty', { defaultValue: 'nuovi' })}</div>
                        )}
                        {Number(it.line_discount_amount || 0) > 0 && (
                          <div className="text-xs text-primary" data-testid="cart-item-discount">{t('itemEdit.discount', { defaultValue: 'Sconto' })}: -{fmtEur(it.line_discount_amount)}</div>
                        )}
                        {it.price_overridden && (
                          <div className="text-xs text-muted-foreground">{t('itemEdit.priceModified', { defaultValue: 'Prezzo modificato' })}</div>
                        )}
                      </div>
                      <div className="font-display font-semibold tabular text-base">{fmtEur(it.line_total)}</div>
                    </div>
                    <div className="flex items-center justify-between mt-2.5">
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="icon" className="h-9 w-9" disabled={!hasPending} onClick={() => updateQty(it, -1)} data-testid="cart-qty-minus"><Minus className="h-4 w-4" /></Button>
                        <span className="w-9 text-center font-semibold tabular text-base" data-testid="cart-qty">{it.qty}</span>
                        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => updateQty(it, 1)} data-testid="cart-qty-plus"><Plus className="h-4 w-4" /></Button>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-primary" onClick={() => setEditItem(it)} data-testid="cart-item-edit" title={t('itemEdit.title', { defaultValue: 'Modifica articolo' })}><Pencil className="h-4 w-4" /></Button>
                        {anySent
                          ? <Button variant="ghost" size="sm" className="h-9 px-3 text-destructive hover:text-destructive" data-testid="cart-void-item" onClick={() => removeItem(it, true)}><Ban className="h-4 w-4 mr-1" /> {t('order.void')}</Button>
                          : <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeItem(it)} data-testid="cart-del-item"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="border-t border-border p-3 space-y-3 shrink-0">
            {order?.id && (
              <DiscountControl order={order} onApplied={(o) => setOrder((prev) => ({ ...prev, ...o }))} className="pb-3 border-b border-border" />
            )}
            {Number(order?.discount_amount || 0) > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('payment.subtotal', { defaultValue: 'Subtotale' })}</span>
                <span className="tabular text-muted-foreground line-through">{fmtEur(order.items_total ?? order.total)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-base">{t('order.total')}</span>
              <span className="font-display text-2xl font-semibold tabular" data-testid="cart-total">{fmtEur(order?.total || 0)}</span>
            </div>
            <Button onClick={confirmOrder} disabled={pendingCount === 0} className="w-full h-14 rounded-xl text-base" data-testid="cart-confirm-order">
              <Send className="h-5 w-5 mr-2" /> {t('sale.confirmOrder', { defaultValue: 'Conferma ordine' })} {pendingCount > 0 ? `(${pendingCount})` : ''}
            </Button>
            <div className="grid grid-cols-2 gap-2.5">
              <Button variant="outline" onClick={requestBill} disabled={items.length === 0} className="h-12 rounded-xl text-base" data-testid="cart-request-bill"><Receipt className="h-5 w-5 mr-2" /> {t('order.bill')}</Button>
              <Button onClick={() => setPayOpen(true)} disabled={items.length === 0} className="h-12 rounded-xl text-base" data-testid="cart-checkout"><CreditCard className="h-5 w-5 mr-2" /> {t('order.pay')}</Button>
            </div>
          </div>
          </div>
        </div>
      </div>

      <ItemEditDialog orderId={order?.id} item={editItem} open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)} onUpdated={(data) => setOrder(data)} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={order} onPaid={onPaid} />
    </div>
  );
}
